import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import firebase from 'firebase/app';
import { usePostHog } from 'posthog-js/react';

import { getMembershipProductsPrices, getSubscriptionsPlans } from '../../utils/service';
import { getSearchParam } from '../../utils/routes';
import { STRIPE_CHECKOUT_SESSION_ID_PARAM, SUBSCRIPTION_FROM_CHECKOUT_PARAM } from '../../config/routes';
import { ONE_TIME_ORDER_STEPS, getOrderSubtotal } from '../../utils/oneTimeOrder';
import {
  getSubscriptionOrderItemsFromStorage,
  setSubscriptionOrderItemsInStorage,
  SUBSCRIPTION_PAYMENT_TYPES,
  getSubscriptionPlanValues,
  getSubscriptionPlanInterval,
} from '../../utils/subscriptions';
import { getFilteredMembershipItems, MEMBERSHIP_DEFAULT_ORDER_ITEMS } from '../../utils/membership';
import { RECYCLING_PRODUCT_TYPES } from '../../utils/tbbRegistration';

const OneTimeOrderContext = createContext();

export const useOneTimeOrder = () => useContext(OneTimeOrderContext);

export const OneTimeOrderProvider = ({ children }) => {
  const { search } = useLocation();
  const posthog = usePostHog();
  const isSignInWithEmailLink = firebase.auth().isSignInWithEmailLink(window.location.href);
  const query = new URLSearchParams(search);
  const isFromCheckout = getSearchParam(query, SUBSCRIPTION_FROM_CHECKOUT_PARAM) === 'true';
  const checkoutSessionId = getSearchParam(query, STRIPE_CHECKOUT_SESSION_ID_PARAM);

  const [step, setStep] = useState(() => {
    if (isFromCheckout && !!checkoutSessionId) {
      return ONE_TIME_ORDER_STEPS.CONFIRMATION;
    }

    return ONE_TIME_ORDER_STEPS.SELECTION;
  });
  const [orderItems, setOrderItems] = useState(() => {
    const orderItemsFromStorage = getSubscriptionOrderItemsFromStorage();
    const defaultOrderItems = orderItemsFromStorage || MEMBERSHIP_DEFAULT_ORDER_ITEMS;

    if (!orderItemsFromStorage) {
      setSubscriptionOrderItemsInStorage(defaultOrderItems);
    }

    const filteredProducts = { ...defaultOrderItems };
    delete filteredProducts[
      RECYCLING_PRODUCT_TYPES.VIRTUAL
    ];

    return filteredProducts;
  });
  const [productPrices, setProductPrices] = useState({});
  const [subscriptionPlan, setSubscriptionPlan] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [address, setAddress] = useState(null);
  const [shippingPrice, setShippingPrice] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);

    const data = await Promise.all([
      getMembershipProductsPrices(),
      getSubscriptionsPlans(),
    ]);

    const {
      data: productsPricesData,
      error: productsPricesError,
    } = data[0];

    const {
      data: subscriptionPlanData,
      error: subscriptionPlanError,
    } = data[1];

    setProductPrices(productsPricesError ? {} : productsPricesData);

    if (subscriptionPlanError) {
      setSubscriptionPlan({});
    } else {
      const { value, prevValue } = getSubscriptionPlanValues(subscriptionPlanData);

      setSubscriptionPlan({
        ...subscriptionPlanData,
        value,
        prevValue,
        interval: getSubscriptionPlanInterval(subscriptionPlanData?.interval),
      });
    }

    setIsLoading(false);
  }, []);

  const onOrderItemsChange = useCallback((itemId, count) => {
    setOrderItems(prev => {
      const newOrderItems =
      [RECYCLING_PRODUCT_TYPES.VIRTUAL, RECYCLING_PRODUCT_TYPES.TECH].includes(itemId)
        ? {
          ...prev,
          [RECYCLING_PRODUCT_TYPES.VIRTUAL]: count,
          [RECYCLING_PRODUCT_TYPES.TECH]: count,
        } : {
          ...prev,
          [itemId]: count,
        };

      setSubscriptionOrderItemsInStorage(newOrderItems);

      return newOrderItems;
    });
  }, []);

  const subtotal = useMemo(() => {
    const membershipItems = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME)
      .map(item => item.id);

    return getOrderSubtotal(orderItems, productPrices, membershipItems);
  }, [orderItems, productPrices]);

  const value = useMemo(() => ({
    isLoading,
    isSignInWithEmailLink,
    isFromCheckout,
    checkoutSessionId,
    step,
    setStep,
    orderItems,
    setOrderItems,
    onOrderItemsChange,
    productPrices,
    subscriptionPlan,
    subtotal,
    address,
    setAddress,
    shippingPrice,
    setShippingPrice,
    posthog,
    searchParams: query,
  }), [
    isLoading,
    isSignInWithEmailLink,
    isFromCheckout,
    checkoutSessionId,
    step,
    orderItems,
    onOrderItemsChange,
    productPrices,
    subscriptionPlan,
    subtotal,
    address,
    shippingPrice,
    posthog,
    search,
  ]);

  useEffect(() => {
    loadData();
  }, []);

  return (
    <OneTimeOrderContext.Provider value={value}>
      {children}
    </OneTimeOrderContext.Provider>
  );
};
